import React from 'react';
import { Collapse } from 'reactstrap';
import { Form, Formik, FormikValues } from 'formik';

import { useStorage } from '~libs/localstorage';
import { formatFormikInitialValues, prepareSearchParam } from '~libs/searchField';

import Button from '~components/Button';
import Icon, { faMagnifyingGlass } from '~components/icon';

import DropdownCheckbox from './DropdownCheckbox';
import { SearchProps } from './props';
import SearchField from './SearchField';

interface ComponentProps {
  isOpen?: boolean;
  fields: SearchProps;
  onSubmit: (values: { [key: string]: any }) => void;
  initialValues?: Record<string, any>;
  storageKey?: string;
}

const SearchBar: React.FC<ComponentProps> = ({
  isOpen = true,
  fields,
  initialValues = {},
  onSubmit,
  storageKey = 'generic',
}) => {
  // Initial filters are set based on the defaultShow property
  const [openFilters, setOpenFilters] = useStorage(
    storageKey,
    Object.entries(fields)
      .filter(([, { defaultShow }]) => defaultShow)
      .map(([key]) => key),
  );

  const formikInitialValues = formatFormikInitialValues({ fields, initialValues });

  // On search, prepare search values based on field type
  const submit = (values: FormikValues) => {
    let params: { [key: string]: any } = {};
    Object.entries(values).forEach(([key, value]) => {
      const definition = fields[key];
      if (!definition || value === null || value.length === 0) return;
      const { type, field } = definition;
      params = {
        ...params,
        ...prepareSearchParam(type, field, value),
      };
    });
    onSubmit(params);
  };

  const definitions = Object.entries(fields);
  const buildPills = (values: FormikValues, setFieldValue: any) =>
    definitions
      .filter(([key, { persist }]) => openFilters.includes(key) || persist)
      .map(([key, props], index) => (
        <SearchField
          key={index}
          id={key}
          value={values[key]}
          className={`search-pill d-flex align-items-center border rounded bg-white me-2 mt-2 mb-2 ${props.type}`}
          setFieldValue={setFieldValue}
          {...props}
        />
      ));

  // Build filters to display, do not include filters which cannot be removed
  const filters = definitions
    .filter(([, { persist }]) => !persist)
    .map(([key, { caption }]) => ({ key, label: caption }));

  return (
    <Collapse isOpen={isOpen}>
      <Formik enableReinitialize initialValues={formikInitialValues} onSubmit={(values) => submit(values)}>
        {({ values, setFieldValue }) => (
          <Form>
            <div className="search-bar d-flex justify-content-between bg-light p-2" data-cy="search-area">
              <div className="d-flex flex-wrap">{buildPills(values, setFieldValue)}</div>
              <div className="d-flex m-2 align-items-center">
                {filters.length > 0 && (
                  <DropdownCheckbox
                    caption="Filters"
                    items={filters}
                    values={openFilters}
                    onClick={setOpenFilters}
                    dataCy="search-filter-button"
                  />
                )}
                <div>
                  <Button size="sm" className="ms-2" type="submit">
                    <Icon className="me-2" icon={faMagnifyingGlass} data-cy="search-go-button" />
                    Go
                  </Button>
                </div>
              </div>
            </div>
          </Form>
        )}
      </Formik>
    </Collapse>
  );
};

export default SearchBar;
